import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";

console.log("Fireworks script loaded");

const handleInAppRedirection = (appUrl: string): void => {
  if (!appUrl?.includes("bajajfinserv.in") && appUrl?.startsWith("https:")) {
    window.prompt(`openInAppWebViewActivity://{"url":"${appUrl}","title":"Bajaj Finserv"}`);
  } else if (appUrl?.includes("bajajsuperapp") && !appUrl?.startsWith("https:")) {
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    let appUrlWithTimestamp = appUrl;
    if (appUrl.includes("SSO")) {
      if (appUrl.includes("?")) {
        appUrlWithTimestamp = `${appUrl}&ts=${timestamp}`;
      } else {
        appUrlWithTimestamp = `${appUrl}?ts=${timestamp}`;
      }
    }
    const windowWithTarsier = window as Window & {
      tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
    };
    windowWithTarsier.tarsier?.invokePrompt(
      `${APP_PROMPTS.IN_APP_REDIRECTION}${appUrlWithTimestamp}`
    );
  } else {
    window.location.assign(appUrl);
  }
};

export const handleBothAppWebRedirection = (unitUrl: string): void => {
  const [webUrl, appUrl] = unitUrl.split("@");
  if (isOpenInApp()) {
    if (appUrl) {
      handleInAppRedirection(appUrl);
      console.log("app only", appUrl);
    }
  } else if (webUrl) {
    const target = "_self";
    window.open(webUrl, target);
    console.log(webUrl, "web only");
  }
};

export const handleRedirection = (unitUrl: string): void => {
  if (isOpenInApp()) {
    handleInAppRedirection(unitUrl);
    console.log(unitUrl, "unit url without @ for app");
  } else {
    const target = "_self";
    window.open(unitUrl, target);
    console.log(unitUrl, "unit url without @ for web");
  }
};

/**
 * Handles the click event for the primary CTA in the Firework embed.
 * It extracts product details and redirects to the appropriate URL based on the app/web context.
 * It also logs the event to the data layer for analytics.
 *
 * @param {Event} event - The click event triggered by the Firework embed.
 */
export const handlePrimaryCtaClick = (event: Event): void => {
  const customEvent = event as CustomEvent;
  const unitUrl = customEvent?.detail?.product?.product_units[0]?.unit_url || "";

  if (unitUrl) {
    console.log("unitUrl", unitUrl);
    if (unitUrl?.includes("@")) {
      handleBothAppWebRedirection(unitUrl);
    } else {
      handleRedirection(unitUrl);
    }
  }
};

/**
 * Firework channel avatar (`img[alt^="Avatar of"]`) and its wrapper are clickable
 * inside open shadow DOM. Block those clicks so they do not navigate away.
 */
const isFireworkChannelAvatarTarget = (event: Event): boolean =>
  event.composedPath().some((node) => {
    if (!(node instanceof Element)) return false;
    if (node instanceof HTMLImageElement) {
      return (node.getAttribute("alt") ?? "").startsWith("Avatar of");
    }
    return Boolean(node.querySelector(":scope > img[alt^='Avatar of']"));
  });

export const handleChannelAvatarClick = (event: Event): void => {
  if (!isFireworkChannelAvatarTarget(event)) return;
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Initialize Fireworks event listeners.
 * This function should be called after the Firework script is loaded.
 */
export const initFireworks = (): void => {
  // Remove existing listener if any to avoid duplicates
  document.removeEventListener("fw:shopping:product-clicked", handlePrimaryCtaClick);
  document.removeEventListener("click", handleChannelAvatarClick, true);

  // Listen for custom product click event and trigger CTA handler
  document.addEventListener("fw:shopping:product-clicked", handlePrimaryCtaClick);
  document.addEventListener("click", handleChannelAvatarClick, true);

  console.log("Fireworks event listeners initialized");
};

/**
 * Cleanup Fireworks event listeners.
 * This function should be called when component unmounts or when cleanup is needed.
 */
export const cleanupFireworks = (): void => {
  document.removeEventListener("fw:shopping:product-clicked", handlePrimaryCtaClick);
  document.removeEventListener("click", handleChannelAvatarClick, true);
  console.log("Fireworks event listeners cleaned up");
};
